// src/features/analytics/useAnalyticsScope.ts

import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { AnalyticsScopeKind } from '@/lib/constants/analytics';
import type { AnalyticsViewScope } from './AnalyticsApi';

export interface UseAnalyticsScopeResult {
  /** Resolved scope; null while the selected customer is still loading. */
  scope: AnalyticsViewScope | null;
  /** Whether the Network scope toggle may be shown at all. */
  networkEligible: boolean;
}

/**
 * Resolve the requested scope kind (from URL search params) against auth state
 * (ADR-003 §3). Account scope follows the platform's selected-customer
 * semantics (own account, or a parent viewing a selected child). Network scope
 * is eligible only when ALL hold: the selected customer IS the authenticated
 * top-level account, serviceRead + subcustomerRead permissions, and at least
 * one subcustomer exists (UX filter; the first four mirror the gateway's
 * canReadNetwork rule). An ineligible request silently resolves to account —
 * a child selection can never reach a network view. The browser never sends
 * parentCid.
 */
export function useAnalyticsScope(requestedKind: AnalyticsScopeKind): UseAnalyticsScopeResult {
  const { authData, selectedCId, selectedCustomer, subcustomers } = useAuth();

  return useMemo(() => {
    const perms = authData?.cpuser.permissions;
    const userCId = authData?.cpuser.core.cId ?? null;

    const networkEligible =
      userCId !== null &&
      selectedCId !== null &&
      selectedCId === userCId &&
      selectedCustomer !== null &&
      !selectedCustomer.cfg.parentCId &&
      !!perms?.serviceRead &&
      !!perms?.subcustomerRead &&
      subcustomers.length > 0;

    const kind: AnalyticsScopeKind =
      requestedKind === 'network' && networkEligible ? 'network' : 'account';
    const cid = kind === 'network' ? userCId : selectedCId;

    return {
      scope: cid !== null ? { kind, cid } : null,
      networkEligible,
    };
  }, [authData, selectedCId, selectedCustomer, subcustomers, requestedKind]);
}
